import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { getCsrfToken } from "../utils"

interface SignInModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  appleAuthUrl: string
  googleAuthUrl: string
  origin?: string
}

export function SignInModal({
  open,
  onOpenChange,
  title = "Sign In Required",
  description = "Please sign in to continue. It will only take a few seconds even if you don't have an account.",
  appleAuthUrl,
  googleAuthUrl,
  origin,
}: SignInModalProps) {
  const csrfToken = typeof document !== "undefined" ? getCsrfToken() : ""

  const appleAction = origin
    ? `${appleAuthUrl}?origin=${encodeURIComponent(origin)}`
    : appleAuthUrl
  const googleAction = origin
    ? `${googleAuthUrl}?origin=${encodeURIComponent(origin)}`
    : googleAuthUrl

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <form method="post" action={appleAction}>
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border-strong bg-card px-4 py-3 text-sm font-medium text-foreground-secondary shadow-sm hover:bg-surface-subtle transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.966 4.475z" />
              </svg>
              <span>Sign in with Apple</span>
            </button>
          </form>

          <form method="post" action={googleAction}>
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border-strong bg-card px-4 py-3 text-sm font-medium text-foreground-secondary shadow-sm hover:bg-surface-subtle transition-colors"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span>Sign in with Google</span>
            </button>
          </form>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
